// Card art renderer. When `card.image` is set, render the locally-stored
// image. Otherwise fall back to the deterministic gradient/holo stand-in,
// which is also kept around as a backdrop so empty space during image load
// or on broken images isn't a blank box.

import { useMemo, useState } from 'react';
import type { CardSummary } from '../lib/types.ts';

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function deriveColor(card: { id: string; jp: string }): string {
  const h = hashStr(card.id + card.jp);
  return `hsl(${h % 360}, 60%, 45%)`;
}

export function CardArt({ card, style }: { card: CardSummary; style?: React.CSSProperties }) {
  const h = useMemo(() => hashStr(card.id + card.jp), [card.id, card.jp]);
  const a = h % 360;
  const b = (h >> 4) % 360;
  const tilt = ((h >> 8) % 30) - 15;
  const pos1x = 20 + ((h >> 1) % 50);
  const pos1y = 20 + ((h >> 5) % 50);
  const pos2x = 40 + ((h >> 9) % 40);
  const pos2y = 30 + ((h >> 11) % 40);
  const baseColor = deriveColor(card);
  const glyph = card.jp.slice(0, 1);

  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(card.image) && !imgFailed;

  return (
    <div className="cardart" style={style}>
      <div className="cardart-base" style={{ background: baseColor }} />
      <div
        className="cardart-holo"
        style={{
          background: `conic-gradient(from ${a}deg at ${pos1x}% ${pos1y}%, hsla(${a},80%,70%,0.55), hsla(${(a + 90) % 360},90%,60%,0.45), hsla(${(a + 200) % 360},80%,55%,0.4), hsla(${a},80%,70%,0.55))`,
          transform: `rotate(${tilt}deg) scale(1.4)`,
        }}
      />
      <div
        className="cardart-blob"
        style={{
          background: `radial-gradient(circle at ${pos2x}% ${pos2y}%, hsla(${b},90%,75%,0.5), transparent 55%)`,
        }}
      />
      <div className="cardart-grain" />
      {showImage && card.image ? (
        <img
          className="cardart-img"
          src={card.image}
          alt={card.jp}
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <>
          <div className="cardart-glyph" aria-hidden="true">
            {glyph}
          </div>
          <div className="cardart-num">{card.num}</div>
        </>
      )}
    </div>
  );
}
